import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CompanyInfo } from '@attendance/shared/schemas/settings';
import { settingsApi } from '../../api/settings.api.js';

/* ===========================================================================
 * THE COMPANY RECORD, AS ONE MORE THING THE PAGE'S SAVE BAR HOLDS.
 *
 * WHY THIS EXISTS. The Company tab used to carry its own Save button while
 * every other settings section saved from the bar at the foot of the page. Two
 * save mechanisms on one screen, and the reader had no way to know which button
 * owned which fields; the previous attempt at a fix renamed the button ("Save
 * company info") and deleted the sentence apologising for it, which left the
 * defect exactly where it was.
 *
 * The separation looked structural and was not. The company record is a
 * different RESOURCE — `PATCH /settings/info`, not `PATCH /settings/:category`
 * — but that is a difference in which request goes out, and the foot bar was
 * already sending one request PER CATEGORY through `Promise.allSettled`. And
 * `/settings/info` takes a PARTIAL body: the route deep-merges over the stored
 * record before validating (see `settings.routes.ts`, D-014), which is the same
 * partial-patch contract `buildPatch` writes for a category. So the company
 * fields can be dirty-tracked like any other field and ride along in the same
 * batch, which is what they now do.
 *
 * WHAT LIVES HERE. The draft — the server record, the reader's unsaved edits
 * over the top, what is dirty, what would block the save, and the exact body to
 * PATCH. Deliberately NOT the mutation: the page owns the one save, so a
 * `useMutation` here would be the second mechanism growing back. It is a hook
 * rather than lines in `SettingsPage.tsx` because that file is over its line
 * budget and may only shrink.
 * ======================================================================== */

/** The fields this tab actually edits. Everything else in the record is echoed
 *  back untouched by the server's merge, so it is not the client's business. */
export interface CompanyFields {
  name: string;
  legalName: string;
  trnNumber: string;
  address: string;
  phone: string;
  currency: string;
}

export type CompanyEdits = Partial<CompanyFields>;

/** The order the fields are asked for; also the order the dirty count counts. */
export const COMPANY_KEYS: readonly (keyof CompanyFields)[] = [
  'name',
  'legalName',
  'trnNumber',
  'phone',
  'address',
  'currency',
];

/**
 * ISO 4217: three letters. The server takes any string, so the guard is here —
 * the value is concatenated onto every payslip figure, where "aed " or an empty
 * string is visible on a document somebody is paid against.
 */
const CURRENCY_RE = /^[A-Za-z]{3}$/;

/** What stops a dirty draft being saved. The page names it beside the Save. */
export type CompanyObstacle = 'name' | 'currency' | null;

export interface CompanyDraft {
  /** The record has been read. Until then there is nothing honest to show. */
  ready: boolean;
  isError: boolean;
  loadError: unknown;
  refetch: () => void;
  /** Server values with the reader's unsaved edits over the top. */
  values: CompanyFields;
  /** Read-only here; the pay rules own it. */
  timezone: string;
  dirty: ReadonlySet<keyof CompanyFields>;
  dirtyCount: number;
  obstacle: CompanyObstacle;
  setValue: (key: keyof CompanyFields, value: string) => void;
  clear: () => void;
  restore: (edits: CompanyEdits) => void;
  /** Body for `PATCH /settings/info` — only the keys that changed. */
  patch: Partial<CompanyInfo> | null;
}

const EMPTY_VALUES: CompanyFields = {
  name: '',
  legalName: '',
  trnNumber: '',
  address: '',
  phone: '',
  currency: '',
};

function serverValues(data: CompanyInfo | undefined): CompanyFields {
  if (!data) return EMPTY_VALUES;
  return {
    name: data.name ?? '',
    legalName: data.legalName ?? '',
    trnNumber: data.trnNumber ?? '',
    address: data.address ?? '',
    phone: data.phone ?? '',
    currency: data.currency ?? '',
  };
}

export function useCompanyDraft(): CompanyDraft {
  const { data, isError, error, refetch } = useQuery({
    queryKey: ['settings', 'info'],
    queryFn: settingsApi.getInfo,
  });
  const [edits, setEdits] = useState<CompanyEdits>({});

  /* NO `useEffect` HYDRATION. The form is the server record read through the
     edits, never a copy of it kept in step by an effect — so there is no window
     in which the boxes hold a stale record, and nothing to overwrite when a
     refetch lands. It is the same `serverValue`/`currentValue` shape the
     catalogue fields on this page already use. */
  const server = useMemo(() => serverValues(data), [data]);
  const values = useMemo(() => ({ ...server, ...edits }), [server, edits]);

  const dirty = useMemo(() => {
    const out = new Set<keyof CompanyFields>();
    if (!data) return out;
    for (const key of COMPANY_KEYS) {
      if (key in edits && edits[key] !== server[key]) out.add(key);
    }
    return out;
  }, [data, edits, server]);

  /* Only a field the reader has actually touched can block the save. A stored
     currency that was never valid must not hold an unrelated pay rule hostage,
     and a Save disabled by a field nobody edited is the dead grey control §3.11
     is written against. */
  const obstacle: CompanyObstacle =
    dirty.has('name') && values.name.trim() === ''
      ? 'name'
      : dirty.has('currency') && !CURRENCY_RE.test(values.currency.trim())
        ? 'currency'
        : null;

  const setValue = useCallback((key: keyof CompanyFields, value: string): void => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clear = useCallback((): void => setEdits({}), []);
  const restore = useCallback(
    (values_: CompanyEdits): void => setEdits((prev) => ({ ...prev, ...values_ })),
    []
  );

  /* ONLY WHAT CHANGED. An empty optional field is `null` (cleared), never `''`
     — the schema's nullable columns say "no value" with null, and the server
     merge keeps every key this body omits. */
  const patch = useMemo((): Partial<CompanyInfo> | null => {
    if (dirty.size === 0 || obstacle) return null;
    const out: Partial<CompanyInfo> = {};
    if (dirty.has('name')) out.name = values.name.trim();
    if (dirty.has('legalName')) out.legalName = values.legalName || null;
    if (dirty.has('trnNumber')) out.trnNumber = values.trnNumber || null;
    if (dirty.has('address')) out.address = values.address || null;
    if (dirty.has('phone')) out.phone = values.phone || null;
    // Upper-cased on the way out: the value is concatenated onto money figures,
    // so "sar 1,200.00" is not acceptable.
    if (dirty.has('currency')) out.currency = values.currency.trim().toUpperCase();
    return out;
  }, [dirty, obstacle, values]);

  return {
    ready: data !== undefined,
    isError,
    loadError: error,
    refetch: () => void refetch(),
    values,
    timezone: data?.timezone ?? '',
    dirty,
    dirtyCount: dirty.size,
    obstacle,
    setValue,
    clear,
    restore,
    patch,
  };
}

/** The unsaved edits themselves, for the failure notice's Retry. */
export function draftEdits(draft: CompanyDraft): CompanyEdits {
  const out: CompanyEdits = {};
  for (const key of draft.dirty) out[key] = draft.values[key];
  return out;
}
